import { app } from "@microsoft/teams-js";
import { TakingSubmitData } from "@teams/cards";
import {
  EmojiReactionType,
  PollyAuthroingTrackingFields,
  QnaReply,
  TeamsRecipientMember,
  TeamsSurveyDraft,
  TeamsSurveyQnaResponse,
  TeamsSurveyResponse,
  TeamsSurveyTypes,
  TeamsUser,
} from "@teams/types";
import {
  QuestionTemplateDTO,
  SharePollyDTO,
  SurveyCreateRequest,
  SurveyDTO,
  SurveyResponsesDTO,
  SurveyResultNotificationType,
  SurveyTriviaCreateDTO,
  SurveyUpdateOptionsDTO,
  TriviaCreateRequest,
} from "@teams/types/api";

import {
  FetchOptions,
  FetchResult,
  deserializeResponse,
  deserializeSurveyDTO,
  encodeApiUrl,
  fetchApiWithContext,
  fetchNoToken,
  fetchWithToken,
  getGroupIdFromContext,
  isSuccess,
} from "../../_shared/services/ChannelApi";

export type ChannelSurveysOptions = {
  includeResponses?: boolean;
  closeAt?: Date;
  filterMeetings?: boolean;
  excludeTypes?: TeamsSurveyTypes[];
  channelMeetingId?: string;
};

export const surveyApi = {
  audienceSurveys: async (
    context: app.Context,
    options: ChannelSurveysOptions
  ): Promise<FetchResult<SurveyDTO[]>> => {
    const groupId = getGroupIdFromContext(context);
    const audienceId = getAudienceIdFromContext(context);

    const isChannelMeeting = context.channel?.id && context.meeting?.id;

    if (groupId === null || audienceId === null) {
      console.error(
        "Unable to load surveys due to missing groupId or audienceId",
        context,
        audienceId
      );
      return [];
    }

    if (isChannelMeeting && context.meeting?.id === null) {
      console.error(
        "Unable to load surveys due to missing meetingId for channel meeting",
        context,
        audienceId
      );
      return [];
    }

    if (isChannelMeeting) {
      options.channelMeetingId = context.meeting?.id;
    }
    const params = paramsFromChannelSurveysOptions(options);

    const surveys: FetchResult<SurveyDTO[]> = await fetchApiWithContext(
      context,
      encodeApiUrl`/v1/team/${groupId}/audience/${audienceId}/surveys`,
      {
        queryParams: params,
      }
    );

    if (isSuccess(surveys)) {
      surveys.forEach(deserializeSurveyDTO);
    }
    return surveys;
  },

  userSurveys: async (
    context: app.Context,
    options: ChannelSurveysOptions
  ): Promise<FetchResult<SurveyDTO[]>> => {
    const params = paramsFromChannelSurveysOptions(options);

    const surveys: FetchResult<SurveyDTO[]> = await fetchApiWithContext(
      context,
      encodeApiUrl`/v1/owner/surveys`,
      {
        queryParams: params,
      }
    );

    if (isSuccess(surveys)) {
      surveys.forEach(deserializeSurveyDTO);
    }
    return surveys;
  },

  userSurveysByAudience: async (
    context: app.Context,
    options: ChannelSurveysOptions
  ): Promise<FetchResult<SurveyDTO[]>> => {
    const audienceId = getAudienceIdFromContext(context);
    if (audienceId === null) {
      console.error("Unable to load surveys due to missing audienceId", context);
      return [];
    }

    const params = new URLSearchParams();
    if (options.includeResponses) {
      params.append("includeResponses", "true");
    }

    const surveys: FetchResult<SurveyDTO[]> = await fetchApiWithContext(
      context,
      encodeApiUrl`/v1/audience/${audienceId}/surveys`,
      {
        queryParams: params,
      }
    );

    if (isSuccess(surveys)) {
      surveys.forEach(deserializeSurveyDTO);
    }
    return surveys;
  },

  survey: async (
    contextOrToken: app.Context | string,
    surveyId: string,
    includeResponses?: boolean
  ): Promise<FetchResult<Error | { survey: SurveyDTO; serverTime: Date }>> => {
    const queryParams = new URLSearchParams();

    if (includeResponses) {
      queryParams.append("includeResponses", "true");
    }

    let response: FetchResult<{
      survey: SurveyDTO;
      serverTime: string;
    }>;

    const uri = encodeApiUrl`/v1/survey/${surveyId}`;

    if (typeof contextOrToken === "string") {
      response = await fetchWithToken(uri, contextOrToken, { queryParams });
    } else {
      response = await fetchApiWithContext(contextOrToken, uri, { queryParams });
    }

    if (isSuccess(response)) {
      deserializeSurveyDTO(response.survey);
      return {
        survey: response.survey,
        serverTime: new Date(response.serverTime),
      };
    }

    return response;
  },

  createSurvey: async (
    context: app.Context,
    draft: TeamsSurveyDraft,
    trackingFields: PollyAuthroingTrackingFields
  ) => {
    const body: SurveyCreateRequest = {
      draft,
      trackingFields,
    };

    const response = await fetchApiWithContext<{ survey: SurveyDTO; scheduleId?: string }>(
      context,
      encodeApiUrl`/v1/survey`,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    );

    if (isSuccess(response) && response.survey) {
      deserializeSurveyDTO(response.survey);
    }

    return response;
  },

  createTrivia: async (
    context: app.Context,
    data: SurveyTriviaCreateDTO,
    trackingFields: PollyAuthroingTrackingFields
  ) => {
    const body: TriviaCreateRequest = {
      data,
      trackingFields,
    };
    const response = await fetchApiWithContext<{ survey: SurveyDTO }>(
      context,
      encodeApiUrl`/v1/survey/trivia`,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    );

    if (isSuccess(response)) {
      deserializeSurveyDTO(response.survey);
    }

    return response;
  },

  updateSurvey: async (
    context: app.Context,
    surveyId: string,
    update: Partial<TeamsSurveyDraft>,
    updateOptions?: Pick<SurveyUpdateOptionsDTO, "notifyUsersOnEdit" | "removeExistingVotesOnEdit">
  ) => {
    const response = await fetchApiWithContext<{ survey: SurveyDTO }>(
      context,
      encodeApiUrl`/v1/survey/${surveyId}`,
      {
        method: "PUT",
        body: JSON.stringify({ survey: update, updateOptions }),
      }
    );

    if (isSuccess(response)) {
      deserializeSurveyDTO(response.survey);
    }

    return response;
  },

  deliverSurvey: (context: app.Context, surveyId: string, deliverAt?: Date, closeAt?: Date) => {
    return fetchApiWithContext<void>(context, encodeApiUrl`/v1/survey/${surveyId}/deliver`, {
      method: "POST",
      body: JSON.stringify({ deliverAt, closeAt }),
    });
  },

  reopenSurvey: (context: app.Context, surveyId: string, closeAt: Date | undefined) => {
    return fetchApiWithContext<void>(context, encodeApiUrl`/v1/survey/${surveyId}/reopen`, {
      method: "PUT",
      body: JSON.stringify({ closeAt }),
    });
  },

  closeSurvey: (context: app.Context, surveyId: string, closeAt?: Date) => {
    return fetchApiWithContext<void>(context, encodeApiUrl`/v1/survey/${surveyId}/close`, {
      method: "PUT",
      body: JSON.stringify({ closeAt }),
    });
  },

  surveyResponses: async (
    context: app.Context,
    surveyId: string
  ): Promise<FetchResult<SurveyResponsesDTO>> => {
    const result = await fetchApiWithContext<SurveyResponsesDTO>(
      context,
      encodeApiUrl`/v1/survey/${surveyId}/responses`
    );
    if (isSuccess(result)) {
      if (result.responses) {
        result.responses.forEach(deserializeResponse);
      }

      if (result.userResponse) {
        deserializeResponse(result.userResponse);
      }
    }

    return result;
  },

  surveyUserResponse: (contextOrApiToken: app.Context | string, surveyId: string) => {
    const url = encodeApiUrl`/v1/survey/${surveyId}/response`;

    if (typeof contextOrApiToken === "string") {
      return fetchWithToken<TeamsSurveyResponse | undefined>(url, contextOrApiToken);
    } else {
      return fetchApiWithContext<TeamsSurveyResponse | undefined>(contextOrApiToken, url);
    }
  },

  questionTemplatesGlobal: (): Promise<Array<QuestionTemplateDTO>> => {
    return fetchNoToken<Array<QuestionTemplateDTO>>(encodeApiUrl`/global/v1/templates/question`);
  },

  addOwner: async (
    context: app.Context,
    surveyId: string,
    user: TeamsUser
  ): Promise<FetchResult<void>> => {
    const update = await fetchApiWithContext<TeamsUser>(
      context,
      encodeApiUrl`/v1/survey/${surveyId}/owner`,
      { method: "post", body: JSON.stringify(user) }
    );

    if (isSuccess(update)) {
      return;
    }

    return update;
  },

  removeOwner: async (
    context: app.Context,
    surveyId: string,
    userAADId: string
  ): Promise<FetchResult<void>> => {
    const update = await fetchApiWithContext<TeamsUser>(
      context,
      encodeApiUrl`/v1/survey/${surveyId}/owner/${userAADId}`,
      { method: "delete" }
    );

    if (isSuccess(update)) {
      return;
    }

    return update;
  },

  vote: (
    contextOrToken: app.Context | string,
    surveyId: string,
    response: TakingSubmitData,
    source: string
  ) => {
    const url = encodeApiUrl`/v1/survey/${surveyId}/response`;
    const options = {
      method: "POST",
      body: JSON.stringify({ response, source }),
    };

    type Return = {
      userResponse: TeamsSurveyResponse;
      missingRequiredIds: string[];
      isUpdatedOrNew: boolean;
    };

    if (typeof contextOrToken === "string") {
      return fetchWithToken<Return>(url, contextOrToken, options);
    } else {
      return fetchApiWithContext<Return>(contextOrToken, url, options);
    }
  },

  notifyAudienceResults: (
    context: app.Context,
    surveyId: string,
    resultType: SurveyResultNotificationType
  ) => {
    return fetchApiWithContext(context, encodeApiUrl`/v1/survey/${surveyId}/notifyresults`, {
      method: "POST",
      body: JSON.stringify({ resultType }),
    });
  },

  draft: (context: app.Context, draftId: string) => {
    return fetchApiWithContext<{ draft: TeamsSurveyDraft }>(
      context,
      encodeApiUrl`/v1/survey/draft/${draftId}`
    );
  },

  updateQnaResponse: (
    context: app.Context,
    surveyId: string,
    responseId: string,
    update: Partial<Pick<TeamsSurveyQnaResponse, "state" | "focused">> & { responseValue?: string }
  ) => {
    return fetchApiWithContext(context, encodeApiUrl`/v1/qna/${surveyId}/response/${responseId}`, {
      method: "PUT",
      body: JSON.stringify(update),
    });
  },

  createQnaReply: (
    context: app.Context,
    surveyId: string,
    responseId: string,
    reply: Pick<QnaReply, "value" | "scope">
  ) => {
    return fetchApiWithContext(
      context,
      encodeApiUrl`/v1/qna/${surveyId}/response/${responseId}/reply`,
      {
        method: "POST",
        body: JSON.stringify(reply),
      }
    );
  },

  toggleEmojiReaction: async (
    context: app.Context,
    surveyId: string,
    responseId: string,
    surveyType: TeamsSurveyTypes,
    reaction: EmojiReactionType
  ) => {
    const url = encodeApiUrl`/v1/${surveyType}/${surveyId}/response/${responseId}/reaction`;
    const options = { method: "POST", body: JSON.stringify({ reaction }) };

    return fetchApiWithContext(context, url, options);
  },

  addRecipient: async (
    contextOrApiToken: string | app.Context,
    surveyId: string,
    member: TeamsRecipientMember,
    trackingMethod: string
  ) => {
    const url = encodeApiUrl`/v1/survey/${surveyId}/recipient`;
    const bodyObj: TeamsRecipientMember & { method: string } = {
      ...member,
      method: trackingMethod,
    };
    const body = JSON.stringify(bodyObj);

    let result: FetchResult<SharePollyDTO>;
    if (typeof contextOrApiToken === "string") {
      result = await fetchWithToken<SharePollyDTO>(url, contextOrApiToken, {
        method: "POST",
        body,
      });
    } else {
      result = await fetchApiWithContext<SharePollyDTO>(contextOrApiToken, url, {
        method: "POST",
        body,
      });
    }
    return result;
  },

  exportSurveyCsv: async (contextOrApiToken: string | app.Context, surveyId: string) => {
    const url = encodeApiUrl`/v1/survey/${surveyId}/export`;

    if (typeof contextOrApiToken === "string") {
      return fetchWithToken<string>(url, contextOrApiToken, undefined, true);
    } else {
      return fetchApiWithContext<string>(contextOrApiToken, url, undefined, true);
    }
  },

  addChoiceToQuestion: async (
    contextOrApiToken: string | app.Context,
    surveyId: string,
    questionId: string,
    choice: string
  ) => {
    const url = encodeApiUrl`/v1/survey/${surveyId}/question/${questionId}/choice`;

    const options: FetchOptions = {
      method: "POST",
      body: JSON.stringify({ choice }),
    };

    if (typeof contextOrApiToken === "string") {
      return fetchWithToken<{ choiceId: string }>(url, contextOrApiToken, options);
    } else {
      return fetchApiWithContext<{ choiceId: string }>(contextOrApiToken, url, options);
    }
  },
};

export const getAudienceIdFromContext = (context: app.Context): string | null => {
  if (context.chat?.id) {
    return context.chat?.id;
  } else if (context.channel?.id) {
    return context.channel?.id;
  }
  return null;
};

const paramsFromChannelSurveysOptions = (options: ChannelSurveysOptions): URLSearchParams => {
  const params = new URLSearchParams();

  if (options.includeResponses) {
    params.append("includeResponses", "true");
  }

  if (options.filterMeetings) {
    params.append("filterMeetings", "true");
  }

  if (options.excludeTypes) {
    params.append("excludeTypes", options.excludeTypes.join(","));
  }

  if (options.closeAt) {
    params.append("closeAt", options.closeAt.toISOString());
  }

  if (options.channelMeetingId) {
    params.append("channelMeetingId", options.channelMeetingId);
  }

  return params;
};
